import { nanoid } from 'nanoid'
import * as crypto from 'crypto'
import {
  EmailVerificationToken,
  EmailVerificationRequest,
  EmailVerificationConfirm,
  EmailVerificationResult
} from './types'
import { StorageAdapter } from '../types'
import { ValidationError, ConflictError, NotFoundError } from '../errors'

/**
 * Email verification token manager
 * Handles generation, validation, and lifecycle of verification tokens
 */
export class EmailVerificationManager {
  private storage: StorageAdapter
  private verificationExpiry: number // Seconds
  private codeLength: number
  private maxAttempts: number

  constructor(
    storage: StorageAdapter,
    options: {
      verificationExpiry?: number
      codeLength?: number
      maxAttempts?: number
    } = {}
  ) {
    this.storage = storage
    this.verificationExpiry = options.verificationExpiry || 3600 // 1 hour default
    this.codeLength = options.codeLength || 6
    this.maxAttempts = options.maxAttempts || 5
  }

  /**
   * Generate a new verification code
   * Returns both the code and its hash for storage
   */
  generateCode(): { code: string; hash: string } {
    // Generate short alphanumeric code (e.g., "A2B4C9")
    const code = this.generateRandomCode(this.codeLength)
    const hash = this.hashCode(code)
    return { code, hash }
  }

  /**
   * Create a new verification token
   */
  async createToken(request: EmailVerificationRequest): Promise<EmailVerificationToken> {
    if (!request.email || typeof request.email !== 'string') {
      throw new ValidationError('Email must be a valid non-empty string')
    }

    if (!request.email.includes('@')) {
      throw new ValidationError('Invalid email format')
    }

    const { code, hash } = this.generateCode()
    const now = new Date()
    const expiresAt = new Date(now.getTime() + this.verificationExpiry * 1000)

    const token: Omit<EmailVerificationToken, 'id'> = {
      email: request.email.toLowerCase(),
      code,
      codeHash: hash,
      type: request.type,
      userId: request.metadata?.userId,
      metadata: request.metadata,
      expiresAt,
      createdAt: now
    }

    // Store in database
    const stored = await this.storage.createEmailVerificationToken(token)

    return stored
  }

  /**
   * Verify a token code
   * Checks if code matches and hasn't expired
   */
  async verifyCode(
    email: string,
    code: string,
    type: string
  ): Promise<EmailVerificationToken> {
    if (!email || typeof email !== 'string') {
      throw new ValidationError('Email must be a non-empty string')
    }

    if (!code || typeof code !== 'string') {
      throw new ValidationError('Verification code must be a non-empty string')
    }

    // Find active token for this email and type
    const tokens = await this.storage.getEmailVerificationTokens(email.toLowerCase(), type)

    if (tokens.length === 0) {
      throw new NotFoundError('Verification token', `${email}:${type}`)
    }

    // Find matching code (latest first)
    const sortedTokens = tokens.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    for (const token of sortedTokens) {
      // Skip if already used
      if (token.usedAt) {
        continue
      }

      // Skip if expired
      if (new Date(token.expiresAt) <= new Date()) {
        continue
      }

      // Check if code matches
      const isMatch = this.verifyCodeHash(code, token.codeHash)
      if (isMatch) {
        // Mark as used
        const used = await this.storage.markEmailVerificationTokenAsUsed(token.id)
        return used
      }
    }

    throw new ValidationError('Invalid or expired verification code')
  }

  /**
   * Get valid token for email and type
   * Returns the latest unused, non-expired token
   */
  async getValidToken(email: string, type: string): Promise<EmailVerificationToken | null> {
    const tokens = await this.storage.getEmailVerificationTokens(email.toLowerCase(), type)

    if (tokens.length === 0) {
      return null
    }

    // Find latest unused, non-expired token
    const sortedTokens = tokens.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )

    for (const token of sortedTokens) {
      if (!token.usedAt && new Date(token.expiresAt) > new Date()) {
        return token
      }
    }

    return null
  }

  /**
   * Check rate limit for code requests
   * Returns true if user can request new code, false if rate limited
   */
  async checkRateLimit(
    email: string,
    maxRequests: number,
    windowSeconds: number
  ): Promise<boolean> {
    const now = new Date()
    const windowStart = new Date(now.getTime() - windowSeconds * 1000)

    const tokens = await this.storage.getEmailVerificationTokens(email.toLowerCase())

    // Count recent requests
    const recentRequests = tokens.filter(t => new Date(t.createdAt) >= windowStart)

    return recentRequests.length < maxRequests
  }

  /**
   * Cleanup expired tokens
   * Can be called periodically to clean database
   */
  async cleanupExpiredTokens(): Promise<number> {
    return this.storage.deleteExpiredEmailVerificationTokens()
  }

  /**
   * Get attempts for a token (failed verifications)
   */
  async getTokenAttempts(tokenId: string): Promise<number> {
    return this.storage.getEmailVerificationTokenAttempts(tokenId)
  }

  /**
   * Increment failed attempts for a token
   */
  async incrementAttempts(tokenId: string): Promise<number> {
    return this.storage.incrementEmailVerificationTokenAttempts(tokenId)
  }

  /**
   * Check if token is locked due to too many failed attempts
   */
  async isTokenLocked(tokenId: string): Promise<boolean> {
    const attempts = await this.getTokenAttempts(tokenId)
    return attempts >= this.maxAttempts
  }

  /**
   * Generate random code
   */
  private generateRandomCode(length: number): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let code = ''
    for (let i = 0; i < length; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return code
  }

  /**
   * Hash a verification code
   */
  private hashCode(code: string): string {
    return crypto.createHash('sha256').update(code).digest('hex')
  }

  /**
   * Verify a code against its hash
   */
  private verifyCodeHash(code: string, hash: string): boolean {
    const codeHash = crypto.createHash('sha256').update(code).digest('hex')
    return codeHash === hash
  }
}

/**
 * Generate a verification link
 */
export function generateVerificationLink(
  appUrl: string,
  email: string,
  code: string,
  type: string,
  redirectUrl?: string
): string {
  const encoded = encodeURIComponent(email)
  const params = new URLSearchParams({
    email: encoded,
    code,
    type
  })

  if (redirectUrl) {
    params.append('redirect', redirectUrl)
  }

  return `${appUrl}/auth/verify-email?${params.toString()}`
}

/**
 * Generate a password reset link
 */
export function generateResetLink(
  appUrl: string,
  email: string,
  code: string,
  redirectUrl?: string
): string {
  const encoded = encodeURIComponent(email)
  const params = new URLSearchParams({
    email: encoded,
    code
  })

  if (redirectUrl) {
    params.append('redirect', redirectUrl)
  }

  return `${appUrl}/auth/reset-password?${params.toString()}`
}

/**
 * Generate a login link (passwordless)
 */
export function generateLoginLink(
  appUrl: string,
  email: string,
  code: string,
  redirectUrl?: string
): string {
  const encoded = encodeURIComponent(email)
  const params = new URLSearchParams({
    email: encoded,
    code
  })

  if (redirectUrl) {
    params.append('redirect', redirectUrl)
  }

  return `${appUrl}/auth/login-link?${params.toString()}`
}
